import React, { useCallback, useMemo } from 'react';
import { DirectionalHint, ITooltipProps, TooltipDelay, TooltipHost, Text, ITooltipHostStyles } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { Link, useHistory } from 'react-router-dom';
import { changeDateFormat, getParagraphFromContent, highlightTags } from 'app/components/helpers';
import useStore, { IStore } from 'app/store';
import './SearchResultList.scss';
import { ArticleType } from 'generated/article-backend';
import { UserDomain } from 'app/enums/UserDomain.enum';
import { SearchResultDocument, SearchResultDocumentResponse, Tag } from 'generated/search-backend';
import { appInsightsService, RouteConstants } from 'app/utils/constants';
import DOMPurify from 'dompurify';

interface ISearchProps {
    listData: SearchResultDocumentResponse;
    isAdminSearch?: boolean;
}

enum HelpRequestTicketStatus {
    'New' = 1,
    'Active' = 2,
    'Inprogress' = 3,
    'Resolved' = 4,
    'Closed' = 5
}

const hostStyles: Partial<ITooltipHostStyles> = {
    root: { display: 'inline-block', borderRadius: '13px' }
};

const calloutStyles = {
    beak: { padding: '0' },
    beakCurtain: { borderRadius: '13px', padding: '0' },
    calloutMain: { borderRadius: '10px' }
};

const SearchResultList: React.FC<ISearchProps> = (props) => {
    const { listData, isAdminSearch } = props;
    const tagTooltipId = useId('tag-tooltip');
    const hashTagTooltipId = useId('hashtag-tooltip');
    const history = useHistory();
    const setSearchValueClear = useStore((state: IStore) => state.setSearchValueClear);
    const setUserSearchValue = useStore((state: IStore) => state.setUserSearchValue);
    const setTagSearchType = useStore((state: IStore) => state.setTagSearchType);

    const getPageLink = useCallback(() => {
        return isAdminSearch ? '/request-detail/' : '/request/';
    }, [isAdminSearch]);

    const notClearSearchValue = useCallback((item: SearchResultDocument) => {
        setSearchValueClear(false);
        appInsightsService.logEvent("SearchResultClick-Listview: clicked a search result", {
            searchResultId: item?.id || item?.unifiedTrackingId,
        });
    }, [setSearchValueClear]);

    const redirectTo = useMemo(() => {
        if (!listData?.document) return '#';
        if (listData.document.articleType === ArticleType.TrainingArticle.toString()) {
            return { pathname: '/portal-training', state: listData.document.id };
        }
        return listData.document.resultType === 'Ticket'
            ? `${getPageLink()}${listData.document.unifiedTrackingId}`
            : `/article/${listData.document.id}`;
    }, [listData?.document?.articleType, listData?.document?.resultType, listData?.document?.id, listData?.document?.unifiedTrackingId, getPageLink]);

    const searchByTag = useCallback((searchValue: string) => {
        setSearchValueClear(false);
        setUserSearchValue(searchValue);
        setTagSearchType('Tag');
        history.push(RouteConstants.SEARCH_RESULTS);
    }, [setSearchValueClear, setUserSearchValue, setTagSearchType, history]);

    const searchByHashTag = useCallback((searchValue: string) => {
        setSearchValueClear(false);
        setUserSearchValue(searchValue);
        setTagSearchType('HashTag');
        history.push(RouteConstants.SEARCH_RESULTS);
    }, [setSearchValueClear, setUserSearchValue, setTagSearchType, history]);

    const tagTooltipProps: ITooltipProps = React.useMemo(
        () => ({
            onRenderContent: () => (
                <ul className="tags-list">
                    {listData?.document?.requestTags?.slice(2).map((item: any) => (
                        <li key={item?.id} onClick={() => searchByTag(item?.name)}>
                            <span className="tags-txt">{item?.name}</span>
                        </li>
                    ))}
                </ul>
            ),
            calloutProps: { styles: calloutStyles }
        }),
        [listData?.document, searchByTag]
    );

    const hashTagTooltipProps: ITooltipProps = React.useMemo(
        () => ({
            onRenderContent: () => (
                <ul className="tags-list">
                    {listData?.document?.hashTags?.slice(3).map((item: any) => (
                        <li key={item?.id} onClick={() => searchByTag(item?.hashTag)}>
                            <span className="tags-txt">{`#${item?.hashTag}`}</span>
                        </li>
                    ))}
                </ul>
            ),
            calloutProps: { styles: calloutStyles }
        }),
        [listData?.document, searchByTag]
    );

    const renderAssociationTag = () => {
        let label = '';
        let tagClass = '';
        if (listData?.document?.isParent) {
            label = 'Parent';
            tagClass = 'result-association Parent';
        }

        if (listData?.document?.parentRequestId != null && listData?.document?.parentRequestId !== "") {
            label = 'Child';
            tagClass = 'result-association Child';
        }

        if (!label) return null;
        return <div className={tagClass}>{label}</div>;
    };

    if (!listData?.document) return null;

    const isTicket = listData.document.resultType === 'Ticket';

    if (isTicket) {
        return (
            <div className="search-result-container" data-status={listData.document.resultType}>
                <Link
                    role="link"
                    onClick={() => notClearSearchValue(listData.document)}
                    to={`${getPageLink()}${listData.document.unifiedTrackingId}`}
                    className="result-title">
                    {listData.document.unifiedTrackingId} -{' '}
                    {listData.highlights?.Summary ? (
                        <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(listData.highlights.Summary?.join(' ') || '') }} />
                    ) : (
                        <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(listData.document?.title) }} />
                    )}
                </Link>
                <div className="custom-row result-section">
                    <div className={`result-status ${HelpRequestTicketStatus[listData.document.status] ?? 'Unknown'}`}>
                        {HelpRequestTicketStatus[listData.document.status] ?? 'Unknown'}
                    </div>
                    {renderAssociationTag()}
                    {listData.document.requestTeam && listData.document.requestTeam > 0 && (
                        <div className="result-tag">{UserDomain[listData.document.requestTeam]}</div>
                    )}
                    {listData.document.requestTags?.length > 0 &&
                        isAdminSearch && (
                        <div className="tags-container">
                            {listData.document.requestTags.slice(0, 2).map((tag: any) => (
                                <div className="tag-display" key={tag?.id} onClick={() => searchByTag(tag?.name)}>
                                    {tag?.name}
                                </div>
                            ))}
                            {listData.document.requestTags.length > 2 && (
                                <div className="d-flex-align-center">
                                    <TooltipHost
                                        tooltipProps={tagTooltipProps}
                                        delay={TooltipDelay.zero}
                                        id={tagTooltipId}
                                        directionalHint={DirectionalHint.bottomCenter}
                                        styles={hostStyles}
                                        className="hostStyles-new">
                                        <Text className="tags-count">+{listData.document.requestTags.length - 2}</Text>
                                    </TooltipHost>
                                </div>
                            )}
                            {listData.document.hashTags?.slice(0, 3).map((tag: any) => (
                                <div className="tag-display" key={tag?.id} onClick={() => searchByHashTag(tag?.hashTag)}>
                                    {`#${tag?.hashTag}`}
                                </div>
                            ))}
                            {listData.document.hashTags?.length > 3 && (
                                <div className="d-flex-align-center">
                                    <TooltipHost
                                        tooltipProps={hashTagTooltipProps}
                                        delay={TooltipDelay.zero}
                                        id={hashTagTooltipId}
                                        directionalHint={DirectionalHint.bottomCenter}
                                        styles={hostStyles}
                                        className="hostStyles-new">
                                        <Text className="tags-count">+{listData.document.hashTags.length - 3}</Text>
                                    </TooltipHost>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                {listData.highlights?.Description ? (
                    <div
                        className="result-description"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(highlightTags(listData.highlights.Description.join(' '))) }}
                    />
                ) : (
                    <div
                        className="result-description"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(highlightTags(listData.document?.description || '')) }}
                    />
                )}
            </div>
        );
    }

    return (
        <>
            <div className="search-list" data-status={listData.document.resultType}>
                <div>
                    <Text>
                        <label
                            className={`badge badge-pill badge-primary  ${listData.document.resultType === 'Ticket' ? 'badge-help' : 'badge-article'}`}
                            tabIndex={0}>
                            {listData.document.resultType === 'Ticket' ? 'Support Requests' : listData.document.resultType}
                        </label>
                    </Text>
                </div>
                <div className="results-list">
                    <div className='result-link'>
                        <Link onClick={() => notClearSearchValue(listData.document)} to={redirectTo}>
                            <h3>{listData.document.title}</h3>
                        </Link>
                    </div>

                    {listData.document.resultType === 'Ticket' && (
                        <p
                            className="results-description"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(`${listData.document.unifiedTrackingId} - ${listData.document.description}`)
                            }}></p>
                    )}

                    {listData.document.resultType !== 'Ticket' && (
                        <p
                            className="results-description"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(getParagraphFromContent(listData?.document?.description))
                            }}></p>
                    )}
                </div>
                <div>
                    <div className="results-social">
                        <span className="text-article results-date">
                            Last Updated : {changeDateFormat(listData.document.createdOn, 'MMMM DD, YYYY')}
                        </span>
                        {listData.document.resultType !== 'Ticket' && listData.document.tags[0]?.displayName && (
                            <div className='results-social-list'>
                                {listData.document.tags.map((item: Tag, index: number) => (
                                    <span key={index} className="text-article">
                                        <span className="results-type">
                                            <span className="article-icon"></span>
                                            {item?.displayName}
                                        </span>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default SearchResultList;
